'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
} from '@mui/material';
import { encodeBase64 } from '../../utils/base64';
import ChevronRightIcon from '@mui/icons-material/ChevronRightOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../components/Navigation';
import RelatedPages from '../../components/RelatedPages';

type Example = {
  title: string;
  description: string;
  badge: string;
  badgeColor: string;
  oldJson: unknown;
  newJson: unknown;
  patch: unknown;
  learnMore?: { label: string; href: string };
};

const addMoveGroups: { heading: string; intro: string; examples: Example[] }[] = [
{
  heading: "Add a field to a JSON object",
  intro: "The add operation inserts a value where none existed - the parent path must exist, the final key must not.",
  examples: [
  {
    title: "Add a New Field",
    description: "Creating a property that did not exist with one add operation.",
    badge: "Objects",
    badgeColor: "var(--accent)",
    oldJson: { "name": "John", "age": 25 },
    newJson: { "name": "John", "age": 25, "email": "john@example.com" },
    patch: [
      { "op": "add", "path": "/email", "value": "john@example.com" }
    ],
    learnMore: { label: "The add operation", href: "/operations/add" }
  }
  ]
},
{
  heading: "Rename, move and copy values",
  intro: "Renaming a key is a move: relocate the value and the old key disappears. Copy duplicates a value without touching the source.",
  examples: [
  {
    title: "Rename a Key",
    description: "The move operation removes a value from one path and adds it at another as a single operation.",
    badge: "Objects",
    badgeColor: "var(--accent)",
    oldJson: { "username": "jdoe", "active": true },
    newJson: { "handle": "jdoe", "active": true },
    patch: [
      { "op": "move", "from": "/username", "path": "/handle" }
    ],
    learnMore: { label: "The move operation", href: "/operations/move" }
  },
  {
    title: "Duplicate a Value",
    description: "The copy operation clones a value to a second location.",
    badge: "Objects",
    badgeColor: "var(--accent)",
    oldJson: {
      "billing": { "address": { "city": "NYC", "zip": "10001" } }
    },
    newJson: {
      "billing": { "address": { "city": "NYC", "zip": "10001" } },
      "shipping": { "address": { "city": "NYC", "zip": "10001" } }
    },
    patch: [
      { "op": "copy", "from": "/billing/address", "path": "/shipping/address" }
    ],
    learnMore: { label: "The copy operation", href: "/operations/copy" }
  }
  ]
}];

const updateGroups: { heading: string; intro: string; examples: Example[] }[] = [
{
  heading: "Update values in a JSON object",
  intro: "The everyday case: change fields in place with replace, and add the ones that are missing.",
  examples: [
  {
    title: "User Profile Update",
    description: "Updating user information with new email and preferences.",
    badge: "Common",
    badgeColor: "var(--accent)",
    oldJson: {
      "id": 123, "name": "John Doe", "email": "john@example.com", "age": 25,
      "preferences": { "theme": "light", "notifications": true }
    },
    newJson: {
      "id": 123, "name": "John Smith", "email": "john.smith@example.com", "age": 26,
      "preferences": { "theme": "dark", "notifications": true, "language": "en" }
    },
    patch: [
      { "op": "replace", "path": "/preferences/theme", "value": "dark" },
      { "op": "add", "path": "/preferences/language", "value": "en" },
      { "op": "replace", "path": "/age", "value": 26 },
      { "op": "replace", "path": "/email", "value": "john.smith@example.com" },
      { "op": "replace", "path": "/name", "value": "John Smith" }
    ],
    learnMore: { label: "The replace operation", href: "/operations/replace" }
  },
  {
    title: "Shopping Cart Operations",
    description: "Adding items and applying discounts to a shopping cart.",
    badge: "E-commerce",
    badgeColor: "var(--success)",
    oldJson: {
      "items": [
        { "id": "item1", "name": "Laptop", "price": 999 },
        { "id": "item2", "name": "Mouse", "price": 25 }
      ],
      "total": 1024, "discount": 0
    },
    newJson: {
      "items": [
        { "id": "item1", "name": "Laptop", "price": 999 },
        { "id": "item2", "name": "Mouse", "price": 25 },
        { "id": "item3", "name": "Keyboard", "price": 75 }
      ],
      "total": 1099, "discount": 10
    },
    patch: [
      { "op": "replace", "path": "/discount", "value": 10 },
      { "op": "replace", "path": "/total", "value": 1099 },
      { "op": "add", "path": "/items/2", "value": { "id": "item3", "name": "Keyboard", "price": 75 } }
    ],
    learnMore: { label: "The add operation", href: "/operations/add" }
  },
  {
    title: "Configuration Management",
    description: "Updating application configuration for production deployment.",
    badge: "DevOps",
    badgeColor: "var(--warning)",
    oldJson: {
      "database": { "host": "localhost", "port": 5432, "ssl": false },
      "cache": { "enabled": true, "ttl": 3600 },
      "features": ["auth", "logging"]
    },
    newJson: {
      "database": { "host": "prod-db.company.com", "port": 5432, "ssl": true, "pool_size": 20 },
      "cache": { "enabled": true, "ttl": 7200 },
      "features": ["auth", "logging", "analytics"]
    },
    patch: [
      { "op": "add", "path": "/features/2", "value": "analytics" },
      { "op": "replace", "path": "/cache/ttl", "value": 7200 },
      { "op": "replace", "path": "/database/ssl", "value": true },
      { "op": "replace", "path": "/database/host", "value": "prod-db.company.com" },
      { "op": "add", "path": "/database/pool_size", "value": 20 }
    ],
    learnMore: { label: "The add operation", href: "/operations/add" }
  }
  ]
},
{
  heading: "JSON Patch array operations",
  intro: "Arrays are where most patches go wrong: indexes shift as you edit, and the /- path appends without knowing the length.",
  examples: [
  {
    title: "Append to an Array",
    description: "Adding an item to the end of an array with the /- path.",
    badge: "Arrays",
    badgeColor: "var(--accent)",
    oldJson: {
      "tags": ["a", "b"]
    },
    newJson: {
      "tags": ["a", "b", "c"]
    },
    patch: [
      { "op": "add", "path": "/tags/-", "value": "c" }
    ],
    learnMore: { label: "JSON Patch and arrays", href: "/docs/json-patch-array" }
  },
  {
    title: "Remove an Array Element",
    description: "Deleting by index - later elements shift one position left.",
    badge: "Arrays",
    badgeColor: "var(--accent)",
    oldJson: {
      "queue": ["first", "second", "third"]
    },
    newJson: {
      "queue": ["first", "third"]
    },
    patch: [
      { "op": "remove", "path": "/queue/1" }
    ],
    learnMore: { label: "JSON Patch and arrays", href: "/docs/json-patch-array" }
  }
  ]
},
{
  heading: "Update nested JSON properties",
  intro: "Deep values are patched with the full JSON Pointer path - one operation per value, no need to resend whole objects.",
  examples: [
  {
    title: "Update a Nested Property",
    description: "Changing one deep value with the full JSON Pointer path.",
    badge: "Nested",
    badgeColor: "var(--warning)",
    oldJson: {
      "user": { "name": "Ana", "address": { "city": "NYC", "zip": "10001" } }
    },
    newJson: {
      "user": { "name": "Ana", "address": { "city": "Boston", "zip": "10001" } }
    },
    patch: [
      { "op": "replace", "path": "/user/address/city", "value": "Boston" }
    ],
    learnMore: { label: "JSON Patch and nested objects", href: "/docs/json-patch-nested-objects" }
  }
  ]
},
{
  heading: "Escape special characters in JSON Pointer paths",
  intro: "Keys that contain / or ~ still work: RFC 6901 escapes them as ~1 and ~0.",
  examples: [
  {
    title: "Escaped JSON Pointer",
    description: "Patching a key that literally contains a slash - /a~1b.",
    badge: "Pointer",
    badgeColor: "var(--success)",
    oldJson: {
      "a/b": { "timeout": 30 }
    },
    newJson: {
      "a/b": { "timeout": 60 }
    },
    patch: [
      { "op": "replace", "path": "/a~1b/timeout", "value": 60 }
    ],
    learnMore: { label: "JSON Pointer escaping", href: "/docs/json-pointer#escaping" }
  }
  ]
},
{
  heading: "Test a value before changing it",
  intro: "The test operation adds a precondition: if the value does not match, evaluation stops at that operation.",
  examples: [
  {
    title: "Test Before You Write",
    description: "Stopping evaluation when the document is not in the expected state.",
    badge: "Safety",
    badgeColor: "var(--success)",
    oldJson: {
      "version": 1, "status": "draft"
    },
    newJson: {
      "version": 2, "status": "published"
    },
    patch: [
      { "op": "test", "path": "/version", "value": 1 },
      { "op": "replace", "path": "/version", "value": 2 },
      { "op": "replace", "path": "/status", "value": "published" }
    ],
    learnMore: { label: "The test operation", href: "/operations/test" }
  },
  {
    title: "Guarded Config Rollout",
    description: "Flip a production flag only if it is still off - test first, then change.",
    badge: "DevOps",
    badgeColor: "var(--warning)",
    oldJson: {
      "database": { "ssl": false, "pool_size": 10 }
    },
    newJson: {
      "database": { "ssl": true, "pool_size": 20 }
    },
    patch: [
      { "op": "test", "path": "/database/ssl", "value": false },
      { "op": "replace", "path": "/database/ssl", "value": true },
      { "op": "replace", "path": "/database/pool_size", "value": 20 }
    ],
    learnMore: { label: "The test operation", href: "/operations/test" }
  }
  ]
}];
const groups = [...addMoveGroups, ...updateGroups];

export default function ExamplesPage() {
  const [codeTab, setCodeTab] = useState<'js' | 'python'>('js');

  const createExampleUrl = (example: Example, mode: 'generate' | 'apply' | 'verify') => {
    const oldEncoded = encodeBase64(JSON.stringify(example.oldJson, null, 2));
    const newEncoded = encodeBase64(JSON.stringify(example.newJson, null, 2));
    const patchEncoded = encodeBase64(JSON.stringify(example.patch, null, 2));
    if (mode === 'apply') return `/?old=${oldEncoded}&patch=${patchEncoded}&mode=apply`;
    if (mode === 'verify') return `/?old=${oldEncoded}&new=${newEncoded}&patch=${patchEncoded}&mode=verify`;
    return `/?old=${oldEncoded}&new=${newEncoded}`;
  };

  return (
    <>
      <Navigation activePage="examples" />

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        {/* Breadcrumb */}
        <Box component="nav" sx={{ display: 'flex', alignItems: 'center', mb: 4, fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)' }}>
          <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, transition: 'color var(--duration-fast)', fontSize: 'var(--font-size-base)' }}>Home</Typography>
          </Link>
          <ChevronRightIcon sx={{ fontSize: '18px', mx: 0.5, color: 'var(--fg-subtle)' }} />
          <Typography component="span" sx={{ fontWeight: 500, color: 'var(--fg)', fontSize: 'var(--font-size-base)' }}>Examples</Typography>
        </Box>

        {/* Hero */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h1" sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 700, letterSpacing: '-0.03em',
            color: 'var(--fg)', mb: 2
          }}>
            JSON Patch Examples
          </Typography>
          <Typography sx={{ fontSize: 'var(--font-size-lg)', color: 'var(--fg-muted)', maxWidth: '48rem', lineHeight: 1.7 }}>
            Real-world JSON Patch scenarios. Load any example into the tool, then generate the patch, apply it to the source, or verify it round-trips.
          </Typography>
        </Box>

        {/* Example groups by intent */}
        {groups.map((group) => (
        <Box key={group.heading} sx={{ mb: 8 }}>
          <Typography variant="h2" sx={{
            fontSize: { xs: '1.5rem', sm: '1.875rem' },
            fontWeight: 700, letterSpacing: '-0.02em',
            color: 'var(--fg)', mb: 1
          }}>
            {group.heading}
          </Typography>
          <Typography sx={{ fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)', maxWidth: '48rem', lineHeight: 1.7, mb: 4 }}>
            {group.intro}
          </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {group.examples.map((example, index) => (
            <Paper key={index} sx={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--surface)',
              boxShadow: 'var(--shadow-xs)',
              overflow: 'hidden',
              transition: 'box-shadow var(--duration-normal) var(--ease-default), border-color var(--duration-normal)',
              '&:hover': {
                boxShadow: 'var(--shadow-md)',
                borderColor: 'var(--border-strong)',
              },
            }}>
              {/* Card Header */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 2,
                px: { xs: 3, md: 4 }, py: 2.5,
                borderBottom: '1px solid var(--border)',
                backgroundColor: 'var(--bg-subtle)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h2" sx={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--fg)' }}>
                    {example.title}
                  </Typography>
                  <Box sx={{
                    px: 1.5, py: 0.25, borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)', fontWeight: 600,
                    color: 'var(--fg)',
                    backgroundColor: 'var(--bg-muted)',
                    border: '1px solid var(--border)',
                  }}>
                    {example.badge}
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--fg-muted)', fontSize: 'var(--font-size-base)' }}>
                  {example.description}
                </Typography>
              </Box>

              {/* Before / After side by side */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 0,
              }}>
                {/* Before */}
                <Box sx={{
                  p: { xs: 3, md: 4 },
                  borderRight: { md: '1px solid var(--border)' },
                  borderBottom: { xs: '1px solid var(--border)', md: 'none' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: 'var(--fg-muted)', flexShrink: 0,
                    }} />
                    <Typography sx={{
                      fontSize: 'var(--font-size-xs)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'var(--fg-muted)',
                    }}>
                      Source JSON
                    </Typography>
                  </Box>
                  <Box className="code-scroll" sx={{
                    backgroundColor: 'var(--code-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', p: 2,
                    fontFamily: 'var(--font-mono), monospace',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--fg-muted)',
                    overflowX: 'auto', maxHeight: '220px',
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <code>{JSON.stringify(example.oldJson, null, 2)}</code>
                    </pre>
                  </Box>
                </Box>

                {/* After */}
                <Box sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: 'var(--accent-text)', flexShrink: 0,
                    }} />
                    <Typography sx={{
                      fontSize: 'var(--font-size-xs)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'var(--accent-text)',
                    }}>
                      Target JSON
                    </Typography>
                  </Box>
                  <Box className="code-scroll" sx={{
                    backgroundColor: 'var(--code-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', p: 2,
                    fontFamily: 'var(--font-mono), monospace',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--fg-muted)',
                    overflowX: 'auto', maxHeight: '220px',
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <code>{JSON.stringify(example.newJson, null, 2)}</code>
                    </pre>
                  </Box>
                </Box>
              </Box>

              {/* Card Footer */}
              <Box sx={{
                px: { xs: 3, md: 4 }, py: 2.5,
                borderTop: '1px solid var(--border)',
                backgroundColor: 'var(--bg-subtle)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 2,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 'var(--font-size-sm)', color: 'var(--fg-muted)' }}>
                    Load this example, then pick a mode
                  </Typography>
                  {example.learnMore && (
                    <Link href={example.learnMore.href} style={{ textDecoration: 'none' }}>
                      <Typography sx={{ fontSize: 'var(--font-size-sm)', color: 'var(--accent-text)', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                        Learn more: {example.learnMore.label}
                      </Typography>
                    </Link>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {(['generate', 'apply', 'verify'] as const).map((mode) => (
                    <Button
                      key={mode}
                      href={createExampleUrl(example, mode)}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '16px' }} />}
                      sx={{
                        px: 3, py: 1,
                        borderRadius: 'var(--radius-full)',
                        fontWeight: 600, fontSize: 'var(--font-size-base)',
                        textTransform: 'capitalize',
                        transition: 'all var(--duration-fast) var(--ease-default)',
                        ...(mode === 'generate' ? {
                          backgroundColor: 'var(--accent-text)',
                          color: 'var(--fg-on-accent)',
                          '&:hover': { backgroundColor: 'var(--accent-hover)' },
                        } : {
                          border: '1px solid var(--border-strong)',
                          color: 'var(--fg)',
                          '&:hover': { borderColor: 'var(--accent-text)', color: 'var(--accent-text)' },
                        }),
                      }}
                    >
                      {mode}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
        </Box>
        ))}

        {/* Using JSON Patch in Your Code */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h2" sx={{
            fontSize: '1.875rem', fontWeight: 700,
            color: 'var(--fg)', mb: 1,
          }}>
            Using JSON Patch in Your Code
          </Typography>
          <Typography sx={{ fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)', mb: 4 }}>
            Get started with JSON Patch in JavaScript or Python in just a few lines.
          </Typography>

          <Paper sx={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            backgroundColor: 'var(--surface)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-xs)',
          }}>
            {/* Tabs */}
            <Box sx={{
              display: 'flex', borderBottom: '1px solid var(--border)',
              backgroundColor: 'var(--bg-subtle)',
            }}>
              {[
                { label: 'JavaScript', key: 'js' as const },
                { label: 'Python', key: 'python' as const },
              ].map((tab) => {
                const isActive = codeTab === tab.key;
                return (
                  <Box
                    key={tab.key}
                    onClick={() => setCodeTab(tab.key)}
                    sx={{
                      px: 3, py: 2, fontSize: 'var(--font-size-base)', fontWeight: 500,
                      color: isActive ? 'var(--accent-text)' : 'var(--fg-muted)',
                      borderBottom: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                      backgroundColor: isActive ? 'var(--surface)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'color var(--duration-fast)',
                      '&:hover': { color: isActive ? 'var(--accent-text)' : 'var(--fg)' },
                    }}
                  >
                    {tab.label}
                  </Box>
                );
              })}
            </Box>

            {/* Code content */}
            <Box sx={{ p: { xs: 3, md: 4 }, backgroundColor: 'var(--code-bg)' }}>
              <pre style={{
                margin: 0, fontFamily: 'var(--font-mono), monospace',
                fontSize: 'var(--font-size-base)', lineHeight: 1.7,
                color: 'var(--fg-muted)', overflowX: 'auto',
              }}>
                {codeTab === 'js' ? (
                  <code>
                    <span style={{ color: '#6d28d9' }}>import</span>{' { compare, applyPatch } '}<span style={{ color: '#6d28d9' }}>from</span>{' '}<span style={{ color: '#4ade80' }}>{`'fast-json-patch'`}</span>{'; '}<span style={{ color: '#64748b' }}>{'// ESM'}</span>{'\n'}
                    <span style={{ color: '#64748b' }}>{'// Generate patch'}</span>{'\n'}
                    <span style={{ color: '#6d28d9' }}>const</span>{' patch = compare(oldObj, newObj);\n'}
                    <span style={{ color: '#64748b' }}>{'// Apply patch'}</span>{'\n'}
                    <span style={{ color: '#6d28d9' }}>const</span>{' result = applyPatch(oldObj, patch).newDocument;'}
                  </code>
                ) : (
                  <code>
                    <span style={{ color: '#6d28d9' }}>import</span>{' jsonpatch\n'}
                    {'\n'}
                    <span style={{ color: '#64748b' }}>{'# Generate patch'}</span>{'\n'}
                    {'patch = jsonpatch.make_patch(old_obj, new_obj)\n'}
                    <span style={{ color: '#64748b' }}>{'# Apply patch'}</span>{'\n'}
                    {'result = jsonpatch.apply_patch(old_obj, patch)\n'}
                    {'\n'}
                    <span style={{ color: '#64748b' }}>{'# pip install jsonpatch'}</span>
                  </code>
                )}
              </pre>
            </Box>
          </Paper>
        </Box>

        {/* CTA */}
        <Box sx={{
          textAlign: 'center', mb: 10,
          py: 6, px: 4,
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          backgroundColor: 'var(--bg-subtle)',
        }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--fg)', mb: 1 }}>
            Ready to create your own patches?
          </Typography>
          <Typography sx={{ fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)', mb: 4 }}>
            Generate, apply, and verify JSON Patch operations - free, right in your browser.
          </Typography>
          <Button
            href="/"
            startIcon={<BuildIcon sx={{ fontSize: '20px' }} />}
            sx={{
              px: 4, py: 1.5, borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-base)', fontWeight: 600,
              backgroundColor: 'var(--accent-text)', color: 'var(--fg-on-accent)',
              textTransform: 'none',
              transition: 'all var(--duration-fast) var(--ease-default)',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Open JSON Patch Generator
          </Button>
        </Box>

        {/* Related Pages */}
        <RelatedPages currentPage="examples" showOperations={true} maxItems={6} />
      </Container>
    </>
  );
                            }
